<template>
  <v-container fluid class="ma-0 pa-0 mt-3">
    <!-- Header con KPI principali -->
    <v-row class="ma-0 pa-2 mb-3">
      <v-col cols="12" sm="6" md v-for="kpi in schede" :key="kpi.id">
        <v-card class="elevation-2">
          <v-card-text class="pa-3">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption text--secondary">{{ kpi.etichetta }}</div>
                <div class="text-h5 font-weight-bold">{{ kpi.valore }}</div>
                <div class="text-caption" :class="classeTendenza(kpi.tendenza)">
                  <v-icon small :color="coloreTendenza(kpi.tendenza)">
                    {{ iconaTendenza(kpi.tendenza) }}
                  </v-icon>
                  {{ Math.abs(kpi.tendenza) }}% {{ etichettaConfronto }}
                </div>
                <div v-if="notaScheda(kpi)" class="text-caption text--secondary">{{ notaScheda(kpi) }}</div>
              </div>
              <v-icon size="40" color="primary">{{ kpi.icona }}</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtri Dashboard -->
    <v-card class="elevation-2 mb-3">
      <v-card-text class="py-2">
        <v-row dense align="center">
          <v-col cols="6" sm="3" md="1">
            <v-select v-model="filtriGrafici.anno" :items="anniDisponibili" label="Anno" dense hide-details />
          </v-col>

          <v-col cols="12" sm="9" md="3">
            <v-autocomplete v-model="filtriGrafici.cliente" :items="clientiFiltro" item-text="ragSoc"
              item-value="codAnagrafica" label="Cliente" dense hide-details clearable
              no-data-text="Nessun dato disponibile" />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-autocomplete v-model="filtriGrafici.operatore" :items="elencoOperatori" label="Operatore" dense
              hide-details clearable multiple small-chips no-data-text="Nessun dato disponibile" />
          </v-col>

          <v-col cols="6" sm="3" md="2">
            <v-select v-model="filtriGrafici.destinazione" :items="destinazioni" label="Destinazione" dense
              hide-details />
          </v-col>

          <v-col cols="6" sm="3" md="1">
            <v-select v-model="filtriGrafici.tipoOre" :items="tipiOre" label="Tipo ore" dense hide-details />
          </v-col>

          <v-col cols="12" md="2" class="d-flex align-center">
            <v-switch v-model="filtriGrafici.soloDaFatturare" label="Solo ore da fatturare" dense hide-details
              class="mt-0" />
            <v-spacer />
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon small v-on="on" @click="dialogCollegamenti = true">
                  <v-badge :value="daCollegare > 0" :content="daCollegare" color="warning" overlap>
                    <v-icon>link</v-icon>
                  </v-badge>
                </v-btn>
              </template>
              <span>Collega le anagrafiche di clienti che hanno cambiato ragione sociale</span>
            </v-tooltip>
            <v-btn icon small @click="aggiornaSubito">
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Tabs per diverse visualizzazioni -->
    <v-tabs v-model="tabAttiva" background-color="primary" dark>
      <v-tab>
        <v-icon left>show_chart</v-icon>
        Andamento
      </v-tab>
      <v-tab>
        <v-icon left>pie_chart</v-icon>
        Composizione
      </v-tab>
      <v-tab>
        <v-icon left>assessment</v-icon>
        Clienti e Resa
      </v-tab>
      <v-tab>
        <v-icon left>compare_arrows</v-icon>
        Confronti
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tabAttiva" class="mt-3" touchless>
      <!-- Tab 1: Andamento -->
      <v-tab-item>
        <tab-andamento-ore :filtri="filtriGrafici" :modalita="modalitaVisualizzazione"
          :aggiornaDati="aggiornaDati" />
      </v-tab-item>

      <!-- Tab 2: Composizione -->
      <v-tab-item>
        <tab-composizione-ore :filtri="filtriGrafici" :modalita="modalitaVisualizzazione"
          :aggiornaDati="aggiornaDati" />
      </v-tab-item>

      <!-- Tab 3: Ore dedicate ai clienti, resa e clienti a rischio -->
      <v-tab-item>
        <tab-clienti-resa :filtri="filtriGrafici" :aggiornaDati="aggiornaDati" />
      </v-tab-item>

      <!-- Tab 4: Confronti -->
      <v-tab-item>
        <tab-confronti-ore :filtri="filtriGrafici" :modalita="modalitaVisualizzazione" :aggiornaDati="aggiornaDati" />
      </v-tab-item>
    </v-tabs-items>

    <dialog-collegamenti-anagrafiche v-model="dialogCollegamenti" :elenco-clienti="elencoClienti"
      @cambiato="aggiornaCollegamenti" />
  </v-container>
</template>

<script>
import { get_kpi_ore, get_ore_totali, get_collegamenti_anagrafiche } from "@/api/api.clienti.js";
import mixinRiepilogo from "./mixinRiepilogo.js";
import mixinRichieste from "./mixinRichieste.js";
import TabAndamentoOre from "./TabAndamentoOre.vue";
import TabComposizioneOre from "./TabComposizioneOre.vue";
import TabClientiResa from "./TabClientiResa.vue";
import TabConfrontiOre from "./TabConfrontiOre.vue";
import DialogCollegamentiAnagrafiche from "./DialogCollegamentiAnagrafiche.vue";

export default {
  name: "DashboardOre",

  components: {
    TabAndamentoOre,
    TabComposizioneOre,
    TabClientiResa,
    TabConfrontiOre,
    DialogCollegamentiAnagrafiche
  },

  mixins: [mixinRiepilogo, mixinRichieste],

  props: {
    elencoClienti: { type: Array, default: () => [] },
    elencoOperatori: { type: Array, default: () => [] }
  },

  data: () => ({
    tabAttiva: 0,
    aggiornaDati: 0,
    modalitaVisualizzazione: 'ore', // 'importi' o 'ore'

    filtriGrafici: {
      anno: new Date().getFullYear(),
      soloDaFatturare: true,
      cliente: "",
      operatore: [],
      destinazione: "Cliente",
      tipoOre: "Lavoro"
    },

    destinazioni: [],
    tipiOre: [],
    anniDisponibili: [],

    // true se l'anno è in corso: le tendenze sono calcolate a parità di periodo
    kpiParziale: false,

    timerFiltri: null,
    oreLavoroTotali: 0,

    // Anagrafiche collegate: le vecchie non compaiono nel filtro Cliente (le statistiche le contano con la nuova)
    dialogCollegamenti: false,
    collegamenti: [],
    daCollegare: 0,

    // Le prime due schede riguardano le ore: "Ore totali" conta tutte le ore registrate (qualsiasi tipo, destinazione
    // e stato di fatturazione), "Ore di lavoro" segue i filtri Destinazione, Tipo ore e "Solo ore da fatturare".
    schede: [
      { id: 'oreTotali', etichetta: 'Ore Totali', valore: '0', tendenza: 0, icona: 'schedule' },
      { id: 'oreLavoro', etichetta: 'Ore di Lavoro', valore: '0', tendenza: 0, icona: 'work_outline' },
      { id: 'importo', etichetta: 'Importo Totale', valore: '€ 0', tendenza: 0, icona: 'euro_symbol' },
      { id: 'media', etichetta: 'Ore Medie Giornaliere', valore: '0', tendenza: 0, icona: 'analytics' },
      { id: 'clienti', etichetta: 'Clienti Attivi', valore: '0', tendenza: 0, icona: 'people' }
    ]
  }),

  computed: {
    clientiFiltro() {
      if (!this.collegamenti.length) return this.elencoClienti;
      const vecchi = new Set(this.collegamenti.map(c => c.codVecchio));
      return this.elencoClienti.filter(c => !vecchi.has(c.codAnagrafica));
    },

    etichettaConfronto() {
      const annoPrecedente = this.filtriGrafici.anno - 1;
      return this.kpiParziale ? `vs stesso periodo ${annoPrecedente}` : `vs ${annoPrecedente}`;
    },

    // Riepilogo dei filtri che determinano le ore di lavoro mostrate
    descrizioneFiltriOre() {
      const f = this.filtriGrafici;
      const parti = [
        !f.destinazione || f.destinazione === 'Tutte' ? 'tutte le destinazioni' : f.destinazione.toLowerCase(),
        f.tipoOre ? f.tipoOre.toLowerCase() : 'tutti i tipi'
      ];
      if (f.soloDaFatturare) parti.push('da fatturare');
      return parti.join(' · ');
    }
  },

  methods: {
    scheda(id) {
      return this.schede.find(s => s.id === id);
    },

    notaScheda(kpi) {
      if (kpi.id === 'oreTotali') return `di cui lavoro ${this.$functions.numberWithCommas(this.oreLavoroTotali.toFixed(0))} h`;
      if (kpi.id === 'oreLavoro') return this.descrizioneFiltriOre;
      return '';
    },

    async caricaDatiKPI() {
      const attuale = this.nuovaRichiesta('kpi');
      const filtri = Object.assign({}, this.filtriGrafici);
      try {
        let res = await get_kpi_ore(filtri);
        if (!attuale()) return;

        if (res && res.data) {
          const dati = res.data;

          this.kpiParziale = dati.parziale;

          this.scheda('oreLavoro').valore = dati.oreTotali.toFixed(0);
          this.scheda('oreLavoro').tendenza = dati.tendenzaOre || 0;

          this.scheda('importo').valore = '€ ' + this.$functions.numberWithCommas(dati.importoTotale.toFixed(2));
          this.scheda('importo').tendenza = dati.tendenzaImporto || 0;

          this.scheda('media').valore = dati.oreMediaGiornaliera.toFixed(2);
          this.scheda('media').tendenza = dati.tendenzaMedia || 0;

          this.scheda('clienti').valore = dati.clientiAttivi.toString();
          this.scheda('clienti').tendenza = dati.tendenzaClienti || 0;
        }
      } catch (error) {
        console.error("Errore caricamento KPI:", error);
      }
    },

    // Ore totali registrate: seguono Anno, Cliente e Operatore (non Destinazione, Tipo ore e "Solo da fatturare")
    async caricaOreTotali() {
      const attuale = this.nuovaRichiesta('oreTotali');
      const filtri = Object.assign({}, this.filtriGrafici);
      try {
        let res = await get_ore_totali(filtri);
        if (!attuale()) return;

        if (res && res.data) {
          this.scheda('oreTotali').valore = res.data.ore.toFixed(0);
          this.scheda('oreTotali').tendenza = res.data.tendenza || 0;
          this.oreLavoroTotali = res.data.oreLavoro;
        }
      } catch (error) {
        console.error("Errore caricamento ore totali:", error);
      }
    },

    // Collegamenti tra anagrafiche: servono a nascondere le vecchie dal filtro e a segnalare quelle ancora da collegare.
    // Se la tabella non esiste ancora la dashboard funziona come prima.
    async caricaCollegamenti() {
      try {
        const res = await get_collegamenti_anagrafiche();
        this.applicaCollegamenti(res && res.data);
      } catch (error) {
        console.error("Errore caricamento collegamenti anagrafiche:", error);
      }
    },

    applicaCollegamenti(stato) {
      this.collegamenti = stato && stato.disponibile ? stato.collegamenti : [];
      this.daCollegare = stato && stato.disponibile ? stato.daCollegare.filter(r => r.suggerimenti.length).length : 0;

      // Un cliente scelto nel filtro che risulta ora collegato è sostituito dalla sua anagrafica nuova
      const scelto = this.collegamenti.find(c => c.codVecchio === this.filtriGrafici.cliente);
      if (scelto) this.filtriGrafici.cliente = scelto.codNuovo;
    },

    // Dopo un collegamento le statistiche cambiano: ricarico tutto
    aggiornaCollegamenti(stato) {
      this.applicaCollegamenti(stato);
      this.aggiornaSubito();
    },

    classeTendenza(tendenza) {
      if (tendenza > 0) return 'success--text';
      return tendenza < 0 ? 'error--text' : 'text--secondary';
    },

    coloreTendenza(tendenza) {
      if (tendenza > 0) return 'success';
      return tendenza < 0 ? 'error' : 'grey';
    },

    iconaTendenza(tendenza) {
      if (tendenza > 0) return 'trending_up';
      return tendenza < 0 ? 'trending_down' : 'trending_flat';
    },

    // Il watcher su aggiornaDati ricarica i KPI; le tab ricaricano i propri dati
    caricaTuttiDati() {
      this.aggiornaDati++;
    },

    // Pulsante di aggiornamento: ricarica subito, senza attendere
    aggiornaSubito() {
      clearTimeout(this.timerFiltri);
      this.caricaTuttiDati();
    },

    cambiaModalita() {
      // Forza aggiornamento dei grafici quando cambia modalità
      this.aggiornaDati++;
    },

    inizializzaAnni() {
      const annoCorrente = new Date().getFullYear();
      this.anniDisponibili = [];

      for (let i = 0; i < 6; i++) {
        this.anniDisponibili.push(annoCorrente - i);
      }
    }
  },

  mounted() {
    this.destinazioni = this.getDestinazioni();
    this.tipiOre = this.getTipiOre();
    this.inizializzaAnni();
    this.caricaDatiKPI();
    this.caricaOreTotali();
    this.caricaCollegamenti();
  },

  beforeDestroy() {
    clearTimeout(this.timerFiltri);
  },

  watch: {
    // Qualsiasi filtro cambi (anche svuotando un campo) tutti i dati vengono ricaricati.
    // L'attesa breve evita una raffica di richieste quando si selezionano più operatori di seguito.
    filtriGrafici: {
      deep: true,
      handler() {
        clearTimeout(this.timerFiltri);
        this.timerFiltri = setTimeout(this.caricaTuttiDati, 350);
      }
    },

    aggiornaDati() {
      this.caricaDatiKPI();
      this.caricaOreTotali();
    }
  }
}
</script>
